import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { executeLegacyImport, fetchLegacyImportStatus } from './api'
import type { ExecuteProdutoInput, ExecuteResult, ProdutoImportavel } from './api'
import { toast } from '@shared/lib/toast/toast-store'

export type ImportStep = 'selection' | 'warnings' | 'result'

export interface UseLegacyImportReturn {
  isVisible: boolean
  step: ImportStep
  produtos: ProdutoImportavel[]
  expiresAt: string | undefined
  selections: Record<string, boolean>
  executeResult: ExecuteResult | null
  isExecuting: boolean
  setStep: (step: ImportStep) => void
  togglePrice: (key: string) => void
  handleExecute: () => void
  handleRemindLater: () => void
  handleClose: () => void
}

export function useLegacyImport(): UseLegacyImportReturn {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<ImportStep>('selection')
  const [executeResult, setExecuteResult] = useState<ExecuteResult | null>(null)
  const [overrides, setOverrides] = useState<Record<string, boolean>>({})
  const [dismissed, setDismissed] = useState(false)

  const { data } = useQuery({
    queryKey: ['legacy-import-status'],
    queryFn: async () => {
      try {
        return await fetchLegacyImportStatus()
      } catch {
        return { status: 'NOT_APPLICABLE' as const }
      }
    },
    enabled: import.meta.env.VITE_LEGACY_IMPORT_ENABLED === 'true',
    staleTime: Infinity,
    retry: false,
  })

  const produtos = useMemo(() => (data?.status === 'OFFERED' ? (data.produtos ?? []) : []), [data])

  const defaultSelections = useMemo(() => {
    const result: Record<string, boolean> = {}
    for (const p of produtos) {
      for (const preco of p.precos) {
        if (!preco.jaImportada) {
          result[`${p.legacyId}|${preco.moeda}|${preco.nomePlano ?? ''}`] = true
        }
      }
    }
    return result
  }, [produtos])

  const selections = { ...defaultSelections, ...overrides }

  const executeMutation = useMutation({
    mutationFn: (inputs: {
      produtos: ExecuteProdutoInput[]
      legacyProdutos: ProdutoImportavel[]
    }) => executeLegacyImport(inputs.produtos, inputs.legacyProdutos),
    onSuccess: (result) => {
      setExecuteResult(result)
      setStep('result')
      void queryClient.invalidateQueries({ queryKey: ['legacy-import-status'] })
      void queryClient.invalidateQueries({ queryKey: ['catalog-products'] })
    },
    onError: () => {
      toast.error('Não foi possível importar os produtos. Tente novamente em instantes.')
    },
  })

  const isVisible = !dismissed && data?.status === 'OFFERED'

  function togglePrice(key: string) {
    setOverrides((prev) => ({ ...prev, [key]: !selections[key] }))
  }

  function handleExecute() {
    const selected: ExecuteProdutoInput[] = []
    const addedIds = new Set<string>()
    const selectedProdutos: ProdutoImportavel[] = []

    for (const [key, isSelected] of Object.entries(selections)) {
      if (!isSelected) continue
      const [legacyId, moeda, nomePlano = ''] = key.split('|')
      if (!legacyId || !moeda) continue
      const produto = produtos.find((p) => p.legacyId === legacyId)
      if (!produto) continue
      const preco = produto.precos.find(
        (pr) => pr.moeda === moeda && (pr.nomePlano ?? '') === nomePlano,
      )
      if (!preco) continue
      selected.push({ legacyId, valor: preco.valor, moeda })
      if (!addedIds.has(legacyId)) {
        selectedProdutos.push(produto)
        addedIds.add(legacyId)
      }
    }

    executeMutation.mutate({ produtos: selected, legacyProdutos: selectedProdutos })
  }

  function handleRemindLater() {
    setDismissed(true)
  }

  function handleClose() {
    setDismissed(true)
    setStep('selection')
    setExecuteResult(null)
  }

  return {
    isVisible,
    step,
    produtos,
    expiresAt: data?.expiresAt,
    selections,
    executeResult,
    isExecuting: executeMutation.isPending,
    setStep,
    togglePrice,
    handleExecute,
    handleRemindLater,
    handleClose,
  }
}
